/**
 * Gallery scene: Settings chrome (?test=maater-ui#settings-chrome)
 *
 * SoT for SettingsSection - matches Basecamp Project folders / Listening.
 * Import: @/components/features/settings/settings-section
 */
import { FolderTree, Key, Shield, User } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  SettingsSection,
  SettingsSectionRow,
} from '@/components/features/settings/settings-section'
import { testIds } from '@/lib/test-ids'

export function SettingsChromeScene() {
  return (
    <div className="space-y-8" data-testid={testIds.maaterUiGallery.settingsChrome}>
      <p className="text-sm text-muted-foreground">
        Shared Settings content chrome. Production SoT:{' '}
        <strong className="font-medium text-foreground">Basecamp Project folders</strong> and
        Listening. Use <code className="text-xs">SettingsSection</code> - do not nest shadcn{' '}
        <code className="text-xs">Card</code> inside Settings panes.
      </p>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Basecamp SoT (Project folders)
        </h3>
        <SettingsSection
          icon={<FolderTree />}
          title="Project folders"
          badge={
            <Badge variant="outline" className="text-xs font-medium text-muted-foreground">
              2 overrides
            </Badge>
          }
          description="Where Basecamp work lands in Maater. Everything ingests under the default folder unless a project override says otherwise."
          actions={
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="Reload demo">
              <span className="sr-only">Reload</span>
            </Button>
          }
        >
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Default ingest folder</label>
            <Input
              readOnly
              value="hop-studios/clients"
              className="h-8 font-mono text-xs"
            />
            <p className="text-[11px] text-muted-foreground">
              A project with no override lands in Hop Studios / clients / project-name / todos
            </p>
          </div>
          <div className="flex justify-end">
            <Button size="sm">Save</Button>
          </div>
        </SettingsSection>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Account (Profile)
        </h3>
        <SettingsSection
          icon={<User />}
          title="Profile"
          description="Shown on workspace member lists, document attribution, and invites."
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end max-w-lg">
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium">Display name</label>
              <Input readOnly value="Rowan" />
            </div>
            <Button size="sm">Save</Button>
          </div>
        </SettingsSection>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Keys split (cloud / custom / extension)
        </h3>
        <SettingsSection
          icon={<Key />}
          title="Cloud providers"
          description="Anthropic, OpenAI, Groq, xAI, and search. Encrypted at rest."
        >
          <SettingsSectionRow className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Anthropic API Key</span>
              <Badge
                variant="outline"
                className="text-green-600 border-green-500/40 bg-green-500/10 text-xs"
              >
                Configured
              </Badge>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs">
              Rotate
            </Button>
          </SettingsSectionRow>
        </SettingsSection>
        <SettingsSection
          title="Custom endpoint"
          description="Point this workspace at Open WebUI, vLLM, LocalAI, or remote Ollama."
        >
          <SettingsSectionRow className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">Custom inference base URL</span>
            <Badge variant="outline" className="text-xs text-muted-foreground">
              Not set
            </Badge>
          </SettingsSectionRow>
        </SettingsSection>
        <SettingsSection
          title="Extension secrets"
          description="Keys for workspace extensions when Connect alone is not enough."
        >
          <SettingsSectionRow className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">Cursor API Key</span>
            <Badge variant="outline" className="text-xs text-muted-foreground">
              Not set
            </Badge>
          </SettingsSectionRow>
        </SettingsSection>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Selected row (on)
        </h3>
        <p className="text-sm text-muted-foreground">
          Enabled / current uses primary wash (`SettingsSectionRow selected`), same family as
          rail and Settings nav. Not amber-950.
        </p>
        <SettingsSection title="Chat Providers" description="Toggle on uses selected surface.">
          <div className="space-y-2">
            <SettingsSectionRow selected className="flex items-center justify-between">
              <span className="text-sm font-medium">Claude (Anthropic)</span>
              <Badge
                variant="outline"
                className="text-green-600 border-green-500/40 bg-green-500/10 text-xs"
              >
                On
              </Badge>
            </SettingsSectionRow>
            <SettingsSectionRow className="flex items-center justify-between">
              <span className="text-sm font-medium">OpenAI (GPT-4o)</span>
              <Badge variant="outline" className="text-xs text-muted-foreground">
                Off
              </Badge>
            </SettingsSectionRow>
          </div>
        </SettingsSection>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Secrets + warning
        </h3>
        <SettingsSection
          variant="warning"
          title="Encryption key rotation"
          description="Secrets are encrypted with your hosting environment's master key. If that key is rotated, all stored secrets must be re-entered."
        />
        <SettingsSection
          icon={<Shield />}
          title="Shared secrets"
          description="Available to all members, flows, and extensions in this workspace."
          actions={
            <Button size="sm" variant="outline">
              Add secret
            </Button>
          }
        >
          <p className="text-sm text-muted-foreground">No shared secrets configured yet.</p>
        </SettingsSection>
        <SettingsSection variant="danger" title="Delete workspace">
          <p className="text-sm text-muted-foreground">
            Permanently delete this workspace and all of its data.
          </p>
          <Button variant="destructive" size="sm">
            Delete workspace
          </Button>
        </SettingsSection>
      </section>
    </div>
  )
}
